import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { trackEvent } from "@/analytics/ga";

/**
 * Public user auth state. The backend httpOnly session cookie is the source
 * of truth; this provider caches the last `/me` response and exposes
 * imperative helpers.
 *
 * {
 *   user:    { user_id, email, name, picture, locale_pref, has_password, has_google } | null
 *   status:  "loading" | "authenticated" | "unauthenticated"
 *   login(email, password)      -> { ok: true } | { ok:false, error:string }
 *   signup({name,email,password,locale_pref}) -> { ok: true } | { ok:false, error:string }
 *   logout()                    -> void
 *   refresh()                   -> user | null
 *   googleExchange(session_id, locale_pref)
 *     -> { status:"signed_in", user } | { status:"onboarding_required", prefill }
 *   googleComplete(name, locale_pref)
 *     -> { ok:true, user } | { ok:false, error }
 * }
 */

// API calls use relative `/api/...` paths. See ContentProvider.jsx for
// the rationale (avoids build-time hostname binding bugs).
const UserAuthContext = createContext(null);

async function readJson(res) {
  try { return await res.json(); } catch { return null; }
}

/**
 * Normalise ANY backend failure into `{ code, message }` where `message` is
 * guaranteed to be a plain string.
 *
 * Why this exists (P1 crash fix): FastAPI/Pydantic returns HTTP 422 with
 * `detail` as an ARRAY of error objects (`[{type, loc, msg, input, ctx}]`).
 * Passing that straight into React state and rendering `{error}` throws
 * "Objects are not valid as a React child" and unmounts the whole tree —
 * that was the blank screen on `/auth/login` for empty/malformed input.
 *
 * `code` lets pages pick a localised, user-friendly message while `message`
 * (backend text, when it is a safe string) remains available as a fallback.
 */
export function normalizeApiError(res, data, fallback = "Request failed.") {
  const status = res && typeof res.status === "number" ? res.status : 0;
  let code = "http";
  if (status === 422 || status === 400) code = "validation";
  else if (status === 401) code = "invalid_credentials";
  else if (status === 409) code = "conflict";
  else if (status === 429) code = "rate_limited";
  else if (status >= 500) code = "server";

  let message = fallback;
  const detail = data && typeof data === "object" ? data.detail : undefined;
  if (typeof detail === "string" && detail.trim()) {
    message = detail;
  } else if (Array.isArray(detail)) {
    const first = detail.find((d) => d && typeof d.msg === "string");
    if (first) {
      // Pydantic prefixes some messages with "Value error, " — strip it so
      // the copy reads like a sentence, not a stack trace.
      message = first.msg.replace(/^Value error,\s*/i, "");
    }
  }
  return { code, status, message: String(message) };
}

function networkFailure(err, fallback) {
  return {
    ok: false,
    code: "network",
    status: 0,
    error: fallback,
    detail: err && err.message ? String(err.message) : "",
  };
}

export function UserAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/auth/me`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`me:${res.status}`);
      const data = await res.json();
      setUser(data.user || null);
      setStatus("authenticated");
      return data.user || null;
    } catch {
      setUser(null);
      setStatus("unauthenticated");
      return null;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    let res;
    try {
      res = await fetch(`/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
    } catch (err) {
      // Offline / DNS / aborted — resolve, never reject, so the form's
      // pending state is always released.
      return networkFailure(err, "We couldn't reach the server. Please try again.");
    }
    const data = await readJson(res);
    if (!res.ok) {
      const n = normalizeApiError(res, data, "Sign in failed.");
      return { ok: false, code: n.code, status: n.status, error: n.message };
    }
    if (!data || !data.user) {
      // Defensive: a 2xx without a user payload is a contract violation.
      return { ok: false, code: "server", status: res.status, error: "Sign in failed." };
    }
    setUser(data.user);
    setStatus("authenticated");
    // GA4 standard `login` event with `method` so password vs. Google
    // sign-ins can be sliced apart in reports.
    trackEvent("login", { method: "password" });
    return { ok: true, user: data.user };
  }, []);

  const signup = useCallback(async ({ name, email, password, locale_pref }) => {
    let res;
    try {
      res = await fetch(`/api/auth/signup`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password, locale_pref }),
      });
    } catch (err) {
      return networkFailure(err, "We couldn't reach the server. Please try again.");
    }
    const data = await readJson(res);
    if (!res.ok) {
      const n = normalizeApiError(res, data, "Sign up failed.");
      return { ok: false, code: n.code, status: n.status, error: n.message };
    }
    if (!data || !data.user) {
      return { ok: false, code: "server", status: res.status, error: "Sign up failed." };
    }
    setUser(data.user);
    setStatus("authenticated");
    // GA4 standard `sign_up` event with `method`.
    trackEvent("sign_up", { method: "password", locale: locale_pref || "" });
    return { ok: true, user: data.user };
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore */
    }
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  const googleExchange = useCallback(async (sessionId, localePref) => {
    let res;
    try {
      res = await fetch(`/api/auth/google/exchange`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, locale_pref: localePref }),
      });
    } catch (err) {
      return networkFailure(err, "Google sign in failed.");
    }
    const data = await readJson(res);
    if (!res.ok) {
      const n = normalizeApiError(res, data, "Google sign in failed.");
      return { ok: false, code: n.code, status: n.status, error: n.message };
    }
    if (data.status === "signed_in" && data.user) {
      setUser(data.user);
      setStatus("authenticated");
      // Google one-tap / SSO path that returns an already-existing
      // account → `login` event with method=google for parity with the
      // password flow.
      trackEvent("login", { method: "google" });
      return { ok: true, status: "signed_in", user: data.user };
    }
    return { ok: true, status: "onboarding_required", prefill: data.prefill };
  }, []);

  const googleComplete = useCallback(async (name, localePref) => {
    let res;
    try {
      res = await fetch(`/api/auth/google/complete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, locale_pref: localePref }),
      });
    } catch (err) {
      return { ok: false, reason: "network", error: err?.message || "" };
    }
    const data = await readJson(res);
    if (!res.ok) {
      const detail = data?.detail;
      if (detail === "onboarding_missing") return { ok: false, reason: "missing" };
      if (detail === "onboarding_expired") return { ok: false, reason: "expired" };
      const n = normalizeApiError(res, data, "Account creation failed.");
      return { ok: false, reason: "http", status: res.status, code: n.code, error: n.message };
    }
    setUser(data.user);
    setStatus("authenticated");
    // Google sign-up completion → new account created via Google.
    trackEvent("sign_up", { method: "google", locale: localePref || "" });
    return { ok: true, user: data.user };
  }, []);

  /** Fetch the prefilled onboarding fields for the in-flight Google
   *  sign-up. Returns one of:
   *    { ok: true,  pending: {...} }
   *    { ok: false, reason: "missing"  }   // no onboarding_pending cookie
   *    { ok: false, reason: "expired"  }   // pending record gone / TTL passed
   *    { ok: false, reason: "network"  }   // fetch threw
   *    { ok: false, reason: "http",   status }   // unexpected status
   *
   *  The reason codes let the onboarding page render a *specific* error
   *  message ("Cookies blocked? Try opening the site directly", "Session
   *  expired, sign in again") instead of a silent spinner or a generic
   *  "expired" notice.
   */
  const getPendingOnboarding = useCallback(async () => {
    let res;
    try {
      res = await fetch(`/api/auth/google/pending`, {
        credentials: "include",
      });
    } catch (err) {
      return { ok: false, reason: "network", error: err?.message || "" };
    }
    if (res.ok) {
      try {
        const pending = await res.json();
        return { ok: true, pending };
      } catch {
        return { ok: false, reason: "http", status: res.status };
      }
    }
    if (res.status === 404) {
      const data = await res.json().catch(() => ({}));
      const detail = (data && data.detail) || "";
      // Backend now returns "onboarding_missing" or "onboarding_expired"
      // verbatim — map them to our canonical reason codes.
      if (detail === "onboarding_missing") {
        return { ok: false, reason: "missing" };
      }
      return { ok: false, reason: "expired" };
    }
    return { ok: false, reason: "http", status: res.status };
  }, []);

  useEffect(() => {
    // On any admin session_id fragment or onboarding in progress we still
    // hit /me first; the worst case is one wasted request.
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      user,
      status,
      login,
      signup,
      logout,
      refresh,
      googleExchange,
      googleComplete,
      getPendingOnboarding,
    }),
    [user, status, login, signup, logout, refresh, googleExchange, googleComplete, getPendingOnboarding]
  );

  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
}

export function useUserAuth() {
  const ctx = useContext(UserAuthContext);
  if (!ctx) throw new Error("useUserAuth must be used within <UserAuthProvider>");
  return ctx;
}

/** Build the SAS-branded Google sign-in URL for the public user flow.
 *
 * The browser is sent straight to our own backend `/api/auth/google/start`,
 * which generates a CSRF state and 302-redirects on to Google. The whole
 * dance is owned by SAS — we no longer pass through Emergent's auth proxy.
 */
export function buildGoogleRedirect(lang) {
  const safeLang = lang === "en" ? "en" : "ar";
  return `/api/auth/google/start?lang=${encodeURIComponent(safeLang)}`;
}
